/**
 * Глобальный поиск — строка в AppBar с dropdown подсказками.
 * Ctrl+K / Cmd+K для фокуса.
 */
import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { useDebounce } from '@/hooks/useDebounce';

interface SearchResult {
  type: string;
  type_label: string;
  id: number;
  title: string;
  subtitle: string;
  url: string;
}

const TYPE_STYLE: Record<string, { bg: string; color: string; icon: string }> = {
  aircraft: { bg: '#E6F1FB', color: '#0C447C', icon: '🛩' },
  flight:   { bg: '#E6F1FB', color: '#0C447C', icon: '✈' },
  pilot:    { bg: '#EAF3DE', color: '#27500A', icon: '👤' },
  airport:  { bg: '#FAEEDA', color: '#633806', icon: '📍' },
  safety:   { bg: '#EEEDFE', color: '#3C3489', icon: '⚠' },
};

const GlobalSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debouncedQ = useDebounce(query, 250);

  const { data, isFetching } = useQuery<{ results: SearchResult[] }>({
    queryKey: ['search-suggest', debouncedQ],
    queryFn: async () => {
      const r = await apiClient.get('/search/suggest/', {
        params: { q: debouncedQ, limit: 5 },
      });
      return r.data;
    },
    enabled: debouncedQ.length >= 2,
    staleTime: 10_000,
  });

  const results = data?.results ?? [];

  // Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const goTo = (url: string) => {
    navigate(url);
    setOpen(false);
    setQuery('');
  };

  const goToSearch = () => {
    if (query.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setOpen(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, maxWidth: 480, display: { xs: 'none', md: 'block' } }}>
      {/* Input */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: open && results.length > 0 ? 'primary.main' : 'transparent',
          borderRadius: open && results.length > 0 ? '8px 8px 0 0' : 2,
          px: 1.25,
          height: 34,
          transition: 'border-color .15s',
        }}
      >
        <Search size={14} style={{ opacity: 0.4, flexShrink: 0 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => query.length >= 2 && setOpen(true)}
          onKeyDown={(e) => e.key === 'Enter' && goToSearch()}
          placeholder="Поиск по системе..."
          sx={{ flex: 1, fontSize: 13 }}
        />
        {isFetching && <CircularProgress size={12} />}
        {query && (
          <X
            size={14}
            style={{ cursor: 'pointer', opacity: 0.35, flexShrink: 0 }}
            onClick={() => {
              setQuery('');
              setOpen(false);
            }}
          />
        )}
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontSize: 10,
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 0.5,
            px: 0.5,
            lineHeight: 1.6,
            flexShrink: 0,
          }}
        >
          ⌘K
        </Typography>
      </Box>

      {/* Dropdown */}
      {open && query.length >= 2 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            border: '1px solid',
            borderColor: 'primary.main',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            zIndex: 1300,
            maxHeight: 420,
            overflow: 'auto',
          }}
        >
          {results.length === 0 && !isFetching && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.disabled">
                Ничего не найдено
              </Typography>
            </Box>
          )}

          {results.length > 0 && (
            <List dense disablePadding>
              {results.map((r, i) => {
                const ts = TYPE_STYLE[r.type] || TYPE_STYLE.aircraft;
                const prev = i > 0 ? results[i - 1].type : null;
                return (
                  <Box key={`${r.type}-${r.id}`}>
                    {prev && prev !== r.type && <Divider sx={{ my: 0.25 }} />}
                    <ListItem
                      onClick={() => goTo(r.url)}
                      sx={{
                        cursor: 'pointer',
                        py: 0.6,
                        px: 1.5,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 0.75,
                            bgcolor: ts.bg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                          }}
                        >
                          {ts.icon}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={r.title}
                        secondary={r.subtitle}
                        primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                        secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
                      />
                      <Chip
                        label={r.type_label}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          bgcolor: ts.bg,
                          color: ts.color,
                          border: 'none',
                          ml: 1,
                          flexShrink: 0,
                        }}
                      />
                    </ListItem>
                  </Box>
                );
              })}

              <Divider />
              <ListItem
                onClick={goToSearch}
                sx={{
                  cursor: 'pointer',
                  py: 0.75,
                  px: 1.5,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Search size={14} />
                </ListItemIcon>
                <ListItemText
                  primary={`Показать все результаты для «${query}»`}
                  primaryTypographyProps={{ fontSize: 12, color: 'primary.main' }}
                />
              </ListItem>
            </List>
          )}
        </Paper>
      )}

      {/* Overlay */}
      {open && (
        <Box
          sx={{ position: 'fixed', inset: 0, zIndex: 1299 }}
          onClick={() => setOpen(false)}
        />
      )}
    </Box>
  );
};

export default GlobalSearch;
